/**
 * LA MINE: on y creuse sans fin, et on s'y epuise vite.
 *
 * POURQUOI ELLE EXISTE. Le minerai n'avait qu'UNE source dans tout le jeu: le
 * coup final sur un rocher, une fois sur trois (`CHANCE_MINERAI`). Les rochers
 * ne poussent qu'en montagne et au glacier, ils demandent quatre coups, et la
 * villa a etage en veut vingt, l'hotel quarante. Le monde s'est donc retrouve a
 * sec de minerai, et les gros chantiers avec lui. La mine est la reponse: une
 * source REGULIERE, qu'on ne peut pas epuiser.
 *
 * CE QUI LA BORNE N'EST PAS LE GISEMENT, C'EST LE MINEUR. Un filon inepuisable
 * qui ne coute rien serait un bouton « donne-moi du minerai »; ce qui donne son
 * prix a chaque coup de pioche, c'est l'ENERGIE — un coup ici coute plus de dix
 * fois un coup de hache, et un bonhomme repose tient une trentaine de secondes
 * avant d'etre a plat. On ne perd rien, on ne meurt pas (la regle du jeu ne
 * change pas): on remonte manger, ou dormir, et on redescend.
 *
 * MODULE PUR: ni DOM, ni horloge, ni hasard a soi. Les des sont passes en
 * argument, exactement comme `produitAction`, parce que c'est le SERVEUR qui
 * les lance (server/inventaire.ts) — une recompense que seul le navigateur sait
 * calculer est une recompense que seul le navigateur peut s'accorder.
 */
import type { Cout } from '../types/items.ts'
import type { Alea } from './alea.ts'
import { clamp } from './alea.ts'
import type { Besoins } from '../types/player.ts'

/**
 * Duree d'un coup de pioche au filon, en secondes.
 *
 * Un peu plus long qu'un coup de hache (0,5 s) et un peu moins qu'une pelletee
 * (1,1 s): on doit sentir l'effort sans que l'aller-retour vers le serveur
 * depasse l'animation, comme pour tous les autres gains.
 */
export const DUREE_COUP_MINE = 1.3

/**
 * Energie depensee par coup de pioche, en points (la jauge va de 0 a 100).
 *
 * 4,5 n'est pas un chiffre rond: a pleine energie il donne VINGT-DEUX coups,
 * soit une trentaine de secondes de piochage — et moins que cela quand on
 * descend deja fatigue, puisque `facteurRecolte` ralentit en plus la cadence.
 * Un coup de recolte ordinaire coute 0,4: la mine coute onze fois plus, et
 * c'est exactement ce qu'on lui demande.
 */
export const ENERGIE_PAR_COUP_MINE = 4.5

/**
 * Sous cette energie, on ne pioche plus.
 *
 * C'est le seul endroit du jeu ou un geste s'arrete pour cause de fatigue, et
 * ce n'est pas une entorse a « on ne meurt jamais, on ne perd jamais rien »: on
 * ne perd RIEN en remontant, le filon sera la, et refuser franchement vaut
 * mieux que laisser piocher a vide un bonhomme qui n'avance plus. Le seuil vaut
 * un coup entier: s'arreter a zero pile laisserait passer un dernier coup a
 * moitie paye.
 */
export const ENERGIE_MINIMALE_MINE = ENERGIE_PAR_COUP_MINE

/** Vrai s'il reste de quoi donner un coup de pioche. */
export function peutMiner(b: Besoins): boolean {
  return b.energie >= ENERGIE_MINIMALE_MINE
}

/** Ce qu'un coup de pioche retire au mineur. Rend les besoins mis a jour. */
export function depenserCoupMine(b: Besoins): Besoins {
  return { faim: b.faim, energie: clamp(b.energie - ENERGIE_PAR_COUP_MINE, 0, 100) }
}

/**
 * Ce qu'un coup de pioche arrache au filon.
 *
 * De la pierre a tous les coups — on casse de la roche —, du minerai trois fois
 * sur cinq, et de temps en temps une belle veine qui en donne deux. Soit trois
 * quarts de minerai par coup en moyenne: une descente complete, energie pleine,
 * rapporte de quoi payer une villa (8) largement, un etage (20) presque, et
 * l'hotel (40) en deux ou trois. C'est le rythme voulu — la mine remplace la
 * chasse au rocher, elle ne remplace pas le chantier.
 *
 * Le lot est BORNE par cette fonction et par elle seule: le client n'annonce
 * jamais ce qu'il a trouve, il annonce qu'il a pioche.
 */
export function produitMine(alea: Alea): Cout {
  const gains: Cout = { pierre: alea.entier(1, 2) }
  if (alea.chance(0.6)) gains.minerai = 1
  if (alea.chance(0.15)) gains.minerai = (gains.minerai ?? 0) + 1
  return gains
}
